import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import Home from "@/pages/Home";
import Admin from "@/pages/Admin";
import Login from "@/pages/Login";
import Setup from "@/pages/Setup";
import AcceptInvite from "@/pages/AcceptInvite";

function Splash({ message }) {
  return (
    <div className="min-h-dvh app-surface flex items-center justify-center">
      <div className="text-white/60 text-sm">{message || "Loading…"}</div>
    </div>
  );
}

function Protected({ children, adminOnly = false }) {
  const { loading, user, needsSetup, isAdmin, error } = useAuth();
  const location = useLocation();

  if (loading) return <Splash />;
  if (error && !user) return <Splash message={error} />;
  if (needsSetup) return <Navigate to="/setup" replace />;
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname }} />;

  if (adminOnly && !isAdmin) {
    return (
      <div className="min-h-dvh bg-slate-950 flex items-center justify-center text-white/50 text-sm">
        Admins only.
      </div>
    );
  }
  return children;
}

/** Keeps signed-in users out of the login and setup screens. */
function SignedOutOnly({ children, allowWhenSetup = false }) {
  const { loading, user, needsSetup } = useAuth();
  if (loading) return <Splash />;
  if (user) return <Navigate to="/" replace />;
  if (needsSetup && !allowWhenSetup) return <Navigate to="/setup" replace />;
  if (!needsSetup && allowWhenSetup) return <Navigate to="/login" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <SignedOutOnly>
            <Login />
          </SignedOutOnly>
        }
      />
      <Route
        path="/setup"
        element={
          <SignedOutOnly allowWhenSetup>
            <Setup />
          </SignedOutOnly>
        }
      />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route
        path="/"
        element={
          <Protected>
            <Home />
          </Protected>
        }
      />
      <Route
        path="/admin"
        element={
          <Protected adminOnly>
            <Admin />
          </Protected>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
      <Toaster theme="dark" position="bottom-center" richColors closeButton />
    </AuthProvider>
  );
}
